import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Plan / entitlements context.
 *
 * Reads `/api/me/entitlements` on auth change. Exposes `plan`, `limits`,
 * `features` to the rest of the app + a `requires(feature)` helper that
 * returns true when the current plan does NOT have the feature
 * (used by Pro-gated controls).
 */
const Ctx = createContext(null);

const FREE_FALLBACK = {
  plan: "free",
  plan_name: "Free",
  seats: 1,
  limits: { workspaces: 2, templates: 10, documents: 10, tabs: 5, key_pairs: 10, version_days: 7, resources_per_node: 1, time_zones: 1 },
  features: { triggers_actions: false, secure_sharing: false, day_tints: false, spotlight_ai: false, highlight_roster: false, custom_fonts: false, otp: false, integrations: false },
};

export function EntitlementsProvider({ children }) {
  // AuthContext exposes `user` (not `token`) — but the api client
  // attaches the bearer token from localStorage automatically, so we
  // just need to know IF a user is logged in.
  const { user } = useAuth();
  const [ent, setEnt] = useState(FREE_FALLBACK);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    if (!user) { setEnt(FREE_FALLBACK); setLoaded(true); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/me/entitlements");
      setEnt(data);
    } catch {
      setEnt(FREE_FALLBACK);
    }
    setLoading(false);
    setLoaded(true);
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const has = (feature) => Boolean(ent?.features?.[feature]);
  const limit = (k) => {
    const v = ent?.limits?.[k];
    if (v === undefined || v === null) return Infinity;
    if (v === -1) return Infinity;
    return v;
  };

  return (
    <Ctx.Provider value={{ ...ent, has, limit, reload, loading, loaded }}>
      {children}
    </Ctx.Provider>
  );
}

export const useEntitlements = () => useContext(Ctx) || FREE_FALLBACK;
